import { useCallback, useEffect, useState, ReactNode, useMemo } from 'react';
import { Redirect, useHistory, useLocation } from 'react-router-dom';
import { useDispatch, useSelector, shallowEqual } from 'react-redux';
import { getLocaleDisplayConfig, HttpStatusCode, isFrontendLocaleType, isSupportedLocaleType, LocalesType } from '@commutifi-fe/constants';
import { GenericError, PageLoader } from '@commutifi-fe/ui';
import { logger } from '@commutifi-fe/logger';
import { BuildingProps } from '@commutifi/models/Buildings';
import { Account } from '@commutifi-fe/interfaces';
import { isUuid, wrapActionInPromise } from 'utils/helpers';
import { DeconstructedPromiseType } from 'shared/interfaces/utils';
import { FetchMeData } from 'shared/interfaces';
import { accountActions, accountSelectors, triggerFetchAccount, updateAccount as updateAccountSaga } from 'store/modules/accounts';
import { ExtendedAccount } from 'shared/interfaces/Account';
import { isFeatureEnabled as isFeatureEnabledRoot } from 'utils/helpers/featureFlags';
import { triggerFetchFeatureFlags } from 'store/modules/enterprise/sagas';
import { enterpriseSelectors } from 'store/modules/enterprise';
import { authActions } from 'store/modules/auth';
import { PublicRouting } from 'src/routing';
import { RootState } from 'store/index';
import { useConfig } from '../ConfigProvider';
import { AccountContext, CurrentAccountContext } from './context';
import { useCurrentAccountWithStalenessCheck, useAccountAnalytics } from './hooks';
import { pick } from 'lodash';
import { extendAccountWithFullNameAndPhoneNumber } from './helpers';
const FETCH_ME_REQUIRED_RELATIONS = ['managedEnterprises', 'enterprise', 'organization', 'building', 'worksite'];
const UNAUTHORIZED_REDIRECT_FALLBACK_MS = 2000;
export function CurrentAccountProvider({
  children,
  urlAccountId
}: {
  children: ReactNode;
  urlAccountId?: string | null;
}) {
  const location = useLocation();
  const history = useHistory();
  const {
    cookieconsent,
    locale: storageLocale,
    changeLocale: changeLocaleConfig
  } = useConfig();
  const {
    currentAccount,
    superAdminViewAccountId,
    email,
    name,
    isFetching,
    error
  } = useSelector((state: RootState) => ({
    currentAccount: accountSelectors.getCurrentAccount(state),
    superAdminViewAccountId: accountSelectors.getSuperAdminViewAccountId(state),
    email: accountSelectors.getCurrentEmail(state),
    name: accountSelectors.getCurrentName(state),
    isFetching: accountSelectors.getIsFetching(state),
    error: accountSelectors.getError(state)
  }), shallowEqual);
  const dispatch = useDispatch();
  const fetchMe = useCallback((promise?: DeconstructedPromiseType<FetchMeData>) => dispatch(accountActions.fetchMeRequest(promise)), [dispatch]);
  const fetchAccount = useCallback((...args: Parameters<typeof triggerFetchAccount>) => dispatch(triggerFetchAccount(...args)), [dispatch]);

  // eslint-disable-next-line react-hooks/preserve-manual-memoization -- React Compiler cannot preserve async callback with dispatch and history
  const logout = useCallback(async () => {
    await wrapActionInPromise((...args_0: Parameters<typeof authActions.logout>) => dispatch(authActions.logout(...args_0)), undefined);
    history.push(PublicRouting.auth.getRoute());
  }, [dispatch, history]);
  const dispatchUpdateAccount = useCallback((...args_1: Parameters<typeof updateAccountSaga>) => dispatch(updateAccountSaga(...args_1)), [dispatch]);

  /**
   * FEATURE FLAGS
   */
  const {
    isFetchingFeatureFlags,
    featureFlagsError,
    featureFlags
  } = useSelector((state_0: RootState) => ({
    isFetchingFeatureFlags: enterpriseSelectors.getIsFetching(state_0),
    featureFlagsError: enterpriseSelectors.getFeatureFlagsError(state_0),
    featureFlags: accountSelectors.getCurrentAccountFeatureFlags(state_0)
  }), shallowEqual);
  const fetchFeatureFlags = useCallback((orgId: string) => {
    if (!orgId) {
      return;
    }
    dispatch(triggerFetchFeatureFlags(orgId));
  }, [dispatch]);
  const fetchFeatureFlagsForAccount = useCallback(() => {
    const orgId_0 = currentAccount?.enterpriseId || currentAccount?.organizationId;
    if (!orgId_0) {
      return;
    }
    fetchFeatureFlags(orgId_0);
  }, [currentAccount?.enterpriseId, currentAccount?.organizationId, fetchFeatureFlags]);
  const isLoadingFeatureFlags = Boolean(isFetchingFeatureFlags && !featureFlags);

  /**
   * Initial /me call and logic to refetch on page reload or when the account is stale
   * for a specific amount of time.
   */
  useCurrentAccountWithStalenessCheck(fetchMe);

  /**
   * Initialize account chat widget, logRocket user identification and Sentry user context.
   */
  useAccountAnalytics({
    currentAccount,
    email,
    name,
    cookieconsent
  });

  // Fix admins case by fetching the current user we try to access (using the id in the URL)
  // If the user is not an admin he won't be able to access that other account.
  useEffect(() => {
    if (currentAccount?.id && urlAccountId && isUuid(urlAccountId) && currentAccount.id !== urlAccountId || superAdminViewAccountId) {
      const accountIdToFetch = urlAccountId || superAdminViewAccountId;
      if (!accountIdToFetch) {
        return;
      }
      fetchAccount(accountIdToFetch, {
        relations: FETCH_ME_REQUIRED_RELATIONS
      });
    }
  }, [currentAccount?.id, urlAccountId, fetchAccount, superAdminViewAccountId]);
  const extendedCurrentAccount = useMemo<ExtendedAccount | undefined>(() => currentAccount ? extendAccountWithFullNameAndPhoneNumber(currentAccount) : undefined, [currentAccount]);
  const locale: `${LocalesType}` | undefined = currentAccount?.preferredLocale || storageLocale;
  const currentLocaleConfig = useMemo(() => locale ? getLocaleDisplayConfig(locale) : undefined, [locale]);

  /**
   * Update the account and return the updated account.
   * If relations are updated, we need to fetch the account with the relations.
   * Otherwise, we can do an optimistic update by merging the updated account with the current account.
   */
  const updateAccount = useCallback(async (accountId: string, accountData: Partial<Account>): Promise<ExtendedAccount> => {
    const normalizedResponse = await wrapActionInPromise(dispatchUpdateAccount, accountId, accountData);
    const updatedAccount = normalizedResponse?.response.entities?.account?.[normalizedResponse.response.result];
    if (accountData.enterpriseId || accountData.organizationId || accountData.buildingId || !extendedCurrentAccount) {
      const meResponse = await wrapActionInPromise(fetchMe);
      return extendAccountWithFullNameAndPhoneNumber(meResponse?.account);
    }
    if (!updatedAccount) {
      return extendedCurrentAccount;
    }
    const optimisticUpdatedAccount = {
      ...extendAccountWithFullNameAndPhoneNumber(updatedAccount),
      // Keep original account relations to avoid losing them
      ...pick(extendedCurrentAccount, FETCH_ME_REQUIRED_RELATIONS),
      // Auth-mode (SSO vs password/OTP) is server-derived state that only /me
      // authoritatively resolves; a field patch can't change it. Keep the
      // current classification rather than trust a mutation response, so the
      // onboarding password step is never re-derived off a PATCH.
      authentication: extendedCurrentAccount.authentication
    };
    return optimisticUpdatedAccount;
  }, [dispatchUpdateAccount, extendedCurrentAccount, fetchMe]);
  const updatePreferredLocale = useCallback(async (l: string): Promise<ExtendedAccount | undefined> => {
    /**
     * Update the account preferred locale if it's a supported locale.
     * Otherwise, update the storage locale to have an effect on the UI without
     * persisting the locale in the account.
     */
    if (extendedCurrentAccount?.id && (isSupportedLocaleType(l) || isFrontendLocaleType(l))) {
      return updateAccount(extendedCurrentAccount.id, {
        preferredLocale: l
      });
    }
    if (isFrontendLocaleType(l)) {
      changeLocaleConfig(l);
    }
    return extendedCurrentAccount;
  }, [changeLocaleConfig, updateAccount, extendedCurrentAccount]);
  const isFeatureEnabled = useMemo(() => isFeatureEnabledRoot(featureFlags), [featureFlags]);
  const accountBuilding = extendedCurrentAccount?.building;
  const building = useMemo<Partial<BuildingProps>>(() => accountBuilding ? accountBuilding : {}, [accountBuilding]);
  const worksiteTimezone = extendedCurrentAccount?.worksite?.defaultTimezone;
  const accountWorksiteTimezone = useMemo<string>(() => worksiteTimezone ?? 'UTC', [worksiteTimezone]);
  const isLoading = isFetching && !currentAccount?.id;

  // Memoize the context value to prevent unnecessary re-renders
  const contextValue = useMemo<AccountContext | null>(() => extendedCurrentAccount ? {
    id: extendedCurrentAccount.id,
    lat: extendedCurrentAccount.location?.lat,
    refreshAccount: fetchMe,
    lng: extendedCurrentAccount.location?.lng,
    currentAccount: extendedCurrentAccount,
    currentLocaleConfig,
    firstName: extendedCurrentAccount.firstName,
    lastName: extendedCurrentAccount.lastName,
    building,
    accountWorksiteTimezone,
    isFetching,
    isLoading,
    error,
    logout,
    updateAccount,
    updatePreferredLocale,
    featureFlagsError,
    featureFlags,
    isFeatureEnabled,
    fetchFeatureFlagsForAccount,
    isLoadingFeatureFlags,
    customBranding: extendedCurrentAccount.customBranding,
    superAdminViewAccountId
  } : null, [extendedCurrentAccount, fetchMe, currentLocaleConfig, building, accountWorksiteTimezone, isFetching, isLoading, error, logout, updateAccount, updatePreferredLocale, featureFlagsError, featureFlags, isFeatureEnabled, fetchFeatureFlagsForAccount, isLoadingFeatureFlags, superAdminViewAccountId]);

  // 401 auth redirects are handled exclusively by PrivateRoutes (app.tsx),
  // which checks getAuthenticatedAccountId() — already cleared by the interceptor's
  // forceLocalLogout() before this component re-renders. Show a loader while
  // PrivateRoutes picks up the redirect to avoid competing <Redirect> components.
  // Safety: if PrivateRoutes doesn't redirect within the timeout, force redirect here.
  const isUnauthorizedError = error?.status === HttpStatusCode.Unauthorized || error?.response?.status === HttpStatusCode.Unauthorized;
  const [unauthorizedTimedOut, setUnauthorizedTimedOut] = useState(false);

  /* eslint-disable react-hooks/set-state-in-effect -- setTimeout-based fallback with cleanup */
  useEffect(() => {
    if (!isUnauthorizedError) {
      setUnauthorizedTimedOut(false);
      return;
    }
    const timeout = setTimeout(() => {
      logger.captureWarning('401 redirect fallback fired — PrivateRoutes did not redirect in time');
      setUnauthorizedTimedOut(true);
    }, UNAUTHORIZED_REDIRECT_FALLBACK_MS);
    return () => clearTimeout(timeout);
  }, [isUnauthorizedError]);
  /* eslint-enable react-hooks/set-state-in-effect */

  if (error) {
    if (isUnauthorizedError) {
      // Fallback 401 redirect (PrivateRoutes normally beats the timeout): carry
      // the requested location so login can resume there — same
      // `resumePathname` contract as PrivateRoutes' redirect, fragment included.
      return unauthorizedTimedOut ? <Redirect to={{
        pathname: PublicRouting.auth.path,
        state: {
          resumePathname: `${location.pathname}${location.search}${location.hash}`
        }
      }} /> : <PageLoader />;
    }
    return <GenericError error={error} type="page" />;
  }
  // If it's fetching and it doesn't have an extendedCurrentAccount it means
  // that we don't have enough information to show the account info and it's probably
  // the first load of the dashboard
  if (!extendedCurrentAccount || !contextValue) {
    return <PageLoader />;
  }
  if (urlAccountId && isUuid(urlAccountId) && urlAccountId === extendedCurrentAccount.id) {
    return <Redirect to={{
      ...location,
      pathname: location.pathname.replace(`/accounts/${urlAccountId}`, '')
    }} />;
  }
  return <CurrentAccountContext value={contextValue}>{children}</CurrentAccountContext>;
}